import { useState, useEffect } from "react";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, API } from "@/App";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { Home, DollarSign, AlertTriangle, CheckCircle, Building, Users, Hotel } from "lucide-react";

export default function HousingGuide() {
  const { t, language } = useLanguage();
  const [housing, setHousing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);

  useEffect(() => {
    loadHousing();
  }, []);

  const loadHousing = async () => {
    try {
      const response = await axios.get(`${API}/content/housing`);
      setHousing(response.data);
    } catch (error) {
      console.error("Failed to load housing:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "apartment": return Building;
      case "room": return Users;
      case "hotel": return Hotel;
      default: return Home;
    }
  };

  if (loading) {
    return (
      <DashboardLayout title={t("دليل السكن", "Housing Guide")}>
        <div className="grid md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="glass-card rounded-xl p-6 h-64 skeleton"></div>
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={t("دليل السكن", "Housing Guide")}>
      <div className="space-y-6" data-testid="housing-guide">
        {/* Header */}
        <div className="glass-card rounded-2xl p-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-64 h-64 bg-green-500/10 blur-[100px] rounded-full"></div>
          <div className="relative">
            <Badge className="bg-green-500/20 text-green-400 mb-4">$5 {t("مرة واحدة", "one-time")}</Badge>
            <h2 className="text-2xl font-bold text-white mb-2">{t("دليل السكن في أمريكا", "Housing Guide in America")}</h2>
            <p className="text-slate-400">{t("كل ما تحتاج معرفته عن أنواع السكن والأسعار", "Everything about housing types and prices")}</p>
          </div>
        </div>

        {/* Warning Card */}
        <div className="glass-card rounded-xl p-5 border-yellow-500/30 flex gap-4">
          <AlertTriangle className="text-yellow-500 flex-shrink-0" size={24} />
          <div>
            <h4 className="font-bold text-yellow-500 mb-1">{t("تحذير مهم!", "Important Warning!")}</h4>
            <p className="text-slate-400 text-sm">
              {t(
                "لا تدفع أي مبلغ قبل رؤية السكن شخصياً. احذر من الإعلانات الوهمية.",
                "Never pay before seeing the property. Beware of fake listings."
              )}
            </p>
          </div>
        </div>

        {/* Housing Types */}
        <div className="grid md:grid-cols-3 gap-6">
          {housing.map((item) => {
            const Icon = getTypeIcon(item.type);
            return (
              <div 
                key={item.housing_id} 
                className="glass-card rounded-xl p-6 cursor-pointer"
                onClick={() => setSelectedType(selectedType === item.housing_id ? null : item.housing_id)}
                data-testid={`housing-${item.housing_id}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 bg-green-500/20 rounded-xl flex items-center justify-center">
                    <Icon className="text-green-400" size={28} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-white">{item.title_ar}</h3>
                    <p className="text-sm text-slate-400">{item.title}</p>
                  </div>
                </div>

                {/* Price */}
                <div className="flex items-center gap-2 mb-4 p-3 bg-slate-800 rounded-lg">
                  <DollarSign className="text-green-400" size={20} />
                  <span className="font-bold text-green-400">{item.price_range}</span>
                </div>

                <p className="text-slate-400 text-sm mb-4">{item.description_ar}</p>

                {/* Expanded Tips */}
                {selectedType === item.housing_id && (
                  <div className="border-t border-slate-700 pt-4 mt-4 animate-fade-in">
                    <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <AlertTriangle size={16} className="text-yellow-500" />
                      {t("نصائح مهمة", "Important Tips")}
                    </h4>
                    <ul className="space-y-2">
                      {item.tips_ar.map((tip, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-400">
                          <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={14} />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="text-center mt-4">
                  <span className="text-sm text-yellow-500 font-medium">
                    {selectedType === item.housing_id ? t("إخفاء", "Hide") : t("عرض النصائح", "Show Tips")}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
}
